import { trackDownload } from '../utils/downloadIndicator';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
  Collapse,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  FileDownload as FileDownloadIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import SignatureCanvas from 'react-signature-canvas';
import { format } from 'date-fns';
import { ramsApi, generatedRamsApi } from '../services/api';
import { renameFile } from '../utils/fileNaming';
import { limitFilesForUpload } from '../utils/uploadLimits';
import RAMSGeneratorSection from './RAMSGeneratorSection';
import GeneratedRAMSEditor from './GeneratedRAMSEditor';

const ProjectRAMTab = ({ projectId, currentUser, projectStatus, onSignatureUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [rams, setRams] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Upload dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadInProgress, setUploadInProgress] = useState(false); // Add flag to prevent double uploads
  const [uploadProgress, setUploadProgress] = useState({}); // Track progress for each file
  
  // Signature dialog state
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [selectedRam, setSelectedRam] = useState(null);
  const [signingFor, setSigningFor] = useState('self');
  const [selectedWorker, setSelectedWorker] = useState('');
  const [signing, setSigning] = useState(false);
  const [signatureError, setSignatureError] = useState(null); // Separate error state for signature dialog
  
  // RAMS preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  
  // Signature preview state
  const [signaturePreviewOpen, setSignaturePreviewOpen] = useState(false);
  const [previewSignature, setPreviewSignature] = useState(null);
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ramToDelete, setRamToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Generated RAMS state
  const [generatedRams, setGeneratedRams] = useState([]);
  const [editingGeneratedRamId, setEditingGeneratedRamId] = useState(null);
  const [editingGeneratedRamData, setEditingGeneratedRamData] = useState(null);
  const [editingHospitalFields, setEditingHospitalFields] = useState({});
  const [editingContractsManagerId, setEditingContractsManagerId] = useState('');
  const [editingSupervisorId, setEditingSupervisorId] = useState('');
  const [editingResponsiblePeopleIds, setEditingResponsiblePeopleIds] = useState([]);
  const [generatedRamSaving, setGeneratedRamSaving] = useState(false);
  const [generatedRamRevising, setGeneratedRamRevising] = useState(false);
  const [generatedRamPdfSaving, setGeneratedRamPdfSaving] = useState({});
  const [allOfficeWorkers, setAllOfficeWorkers] = useState([]);
  const [allSiteWorkers, setAllSiteWorkers] = useState([]);
  
  const signatureRef = useRef(null);

  // Memoize computed values to prevent recalculation on every render
  const computedValues = useMemo(() => ({
    canUploadRAM: currentUser?.role === 'Office',
    canSignRAM: projectStatus === 'Sales Order'
  }), [currentUser?.role, projectStatus]);

  // Check if current user can upload RAMS (admin only)
  const canUploadRAM = computedValues.canUploadRAM;
  
  // Check if current user can sign RAMS (site users on Sales Order projects)
  const canSignRAM = computedValues.canSignRAM;

  // Optimized handlers to prevent unnecessary re-renders
  const handleWorkerNameChange = useCallback((e) => {
    setSelectedWorker(e.target.value);
    // Clear signature error when user starts typing (but don't clear general errors)
    setSignatureError(prev => {
      if (prev && prev.includes('name')) {
        return null;
      }
      return prev;
    });
  }, []);

  const handleSigningForChange = useCallback((e) => {
    setSigningFor(e.target.value);
    setSignatureError(null); // Clear errors when changing signing option
    if (e.target.value === 'self') {
      setSelectedWorker(''); // Clear worker name when switching to self
    }
  }, []);

  const handleSignatureDialogClose = useCallback(() => {
    setSignatureDialogOpen(false);
    setSelectedRam(null);
    setSigningFor('self');
    setSelectedWorker('');
    setSignatureError(null); // Clear signature-specific errors
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  }, []);

  const handleClearSignature = useCallback(() => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  }, []);
  
  // Memoize the RAMS signature requirement check to prevent unnecessary recalculations
  const isRAMSignatureRequiredMemo = useMemo(() => {
    if ((currentUser?.role !== 'Site' && currentUser?.role !== 'Site Manager') || projectStatus !== 'Sales Order') {
      return false;
    }
    
    // If still loading, assume signature is required to block access
    if (loading) {
      return true;
    }
    
    // Check if ALL RAMs have been signed by current user
    if (rams.length === 0) {
      return false; // No RAMs means no signature required
    }
    
    // Get all signatures by the current user for this project
    const userSignatures = signatures.filter(sig => sig.worker_id === currentUser.worker_id);
    const signedRamIds = userSignatures.map(sig => sig.ram_id);
    
    // Check if user has signed ALL RAMS documents
    const allRamsSigned = rams.every(ram => signedRamIds.includes(ram.ram_id));
    
    return !allRamsSigned;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role, currentUser?.worker_id, projectStatus, loading, rams.length, signatures]);
  

  // Check if RAMS signature is required before accessing other tabs
  const isRAMSignatureRequired = useCallback(() => {
    return isRAMSignatureRequiredMemo;
  }, [isRAMSignatureRequiredMemo]);

  useEffect(() => {
    fetchRAMData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Handle signature canvas resize
  useEffect(() => {
    const handleResize = () => {
      if (signatureRef.current && signatureDialogOpen) {
        const canvas = signatureRef.current.getCanvas();
        const container = canvas.parentElement;
        if (container) {
          // Get the actual container dimensions
          const containerRect = container.getBoundingClientRect();
          const containerWidth = containerRect.width;
          
          // Set canvas internal dimensions
          canvas.width = containerWidth;
          canvas.height = 200;
          
          // Update canvas style to match
          canvas.style.width = '100%';
          canvas.style.height = '200px';
          
          // Force redraw
          signatureRef.current.clear();
        }
      }
    };

    if (signatureDialogOpen) {
      window.addEventListener('resize', handleResize);
      // Initial resize after dialog opens with multiple attempts
      setTimeout(handleResize, 50);
      setTimeout(handleResize, 150);
      setTimeout(handleResize, 300);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [signatureDialogOpen]);


  const fetchRAMData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [ramsResponse, signaturesResponse, workersResponse] = await Promise.all([
        ramsApi.getByProjectId(projectId),
        ramsApi.getSignatures(projectId),
        ramsApi.getProjectWorkers(projectId)
      ]);
      
      console.log('RAMs loaded:', ramsResponse.data);
      console.log('Signatures loaded:', signaturesResponse.data);
      console.log('Workers loaded:', workersResponse.data);
      console.log('Current user:', currentUser);
      console.log('Project status:', projectStatus);
      
      // Debug signature structure
      if (signaturesResponse.data.length > 0) {
        console.log('First signature structure:', signaturesResponse.data[0]);
        console.log('Signature keys:', Object.keys(signaturesResponse.data[0]));
        console.log('Signature data preview:', signaturesResponse.data[0].signature_data?.substring(0, 50) + '...');
      }
      
      setRams(ramsResponse.data);
      setSignatures(signaturesResponse.data);
      setWorkers(workersResponse.data.filter(w => w.role === 'Site')); // Only site users need to sign

      // Fetch generated RAMS if Office user
      if (currentUser?.role === 'Office') {
        try {
          const genRes = await generatedRamsApi.getByProjectId(projectId);
          setGeneratedRams(genRes.data || []);
        } catch (genErr) {
          console.error('Error fetching generated RAMS:', genErr);
        }
      }
      
    } catch (err) {
      console.error('Error fetching RAMS data:', err);
      setError('Failed to load RAMS data');
    } finally {
      setLoading(false);
    }
  };

  // ── Generated RAMS handlers ───────────────────────────────────────

  const handleGeneratedRamsRefresh = useCallback(async () => {
    try {
      const genRes = await generatedRamsApi.getByProjectId(projectId);
      setGeneratedRams(genRes.data || []);
    } catch (err) {
      console.error('Error refreshing generated RAMS:', err);
    }
  }, [projectId]);

  const handleEditGeneratedRam = useCallback((gram) => {
    if (editingGeneratedRamId === gram.generated_ram_id) {
      // Collapse
      setEditingGeneratedRamId(null);
      setEditingGeneratedRamData(null);
      return;
    }
    setEditingGeneratedRamId(gram.generated_ram_id);
    setEditingGeneratedRamData({
      method_statements: gram.method_statements || [],
      risk_assessment: gram.risk_assessment || [],
      plant_equipment: gram.plant_equipment || [],
    });
    setEditingHospitalFields({
      hospital_name: gram.hospital_name || '',
      hospital_distance: gram.hospital_distance || '',
      hospital_address: gram.hospital_address || '',
      hospital_opening_times: gram.hospital_opening_times || '',
      hospital_phone: gram.hospital_phone || '',
    });
    setEditingContractsManagerId(gram.contracts_manager_worker_id || '');
    setEditingSupervisorId(gram.supervisor_worker_id || '');
    setEditingResponsiblePeopleIds(
      (gram.responsible_people_worker_ids || '')
        .split(',')
        .map(id => id.trim())
        .filter(Boolean)
    );
    // Load worker lists if not yet loaded
    if (allOfficeWorkers.length === 0) {
      generatedRamsApi.getContext(projectId).then(res => {
        const w = res.data?.workers || {};
        setAllOfficeWorkers(w.office_workers || []);
        setAllSiteWorkers(w.site_workers || []);
      }).catch(() => {});
    }
  }, [editingGeneratedRamId, projectId, allOfficeWorkers.length]);

  const handleSaveGeneratedRam = useCallback(async () => {
    if (!editingGeneratedRamId || !editingGeneratedRamData) return;
    setGeneratedRamSaving(true);
    try {
      await generatedRamsApi.update(editingGeneratedRamId, {
        ...editingGeneratedRamData,
        contracts_manager_worker_id: editingContractsManagerId || null,
        supervisor_worker_id: editingSupervisorId || null,
        responsible_people_worker_ids: editingResponsiblePeopleIds.join(','),
        ...editingHospitalFields,
      });
      await handleGeneratedRamsRefresh();
      setEditingGeneratedRamId(null);
      setEditingGeneratedRamData(null);
    } catch (err) {
      console.error('Error saving generated RAM:', err);
      setError('Failed to save generated RAMS');
    } finally {
      setGeneratedRamSaving(false);
    }
  }, [editingGeneratedRamId, editingGeneratedRamData, editingContractsManagerId, editingSupervisorId, editingResponsiblePeopleIds, editingHospitalFields, handleGeneratedRamsRefresh]);

  const handleDeleteGeneratedRam = useCallback(async (generatedRamId) => {
    try {
      await generatedRamsApi.delete(generatedRamId);
      setEditingGeneratedRamId(null);
      setEditingGeneratedRamData(null);
      setEditingResponsiblePeopleIds([]);
      await handleGeneratedRamsRefresh();
    } catch (err) {
      console.error('Error deleting generated RAM:', err);
      setError('Failed to delete generated RAMS');
    }
  }, [handleGeneratedRamsRefresh]);

  const handleReviseGeneratedRam = useCallback(async (comments) => {
    if (!editingGeneratedRamId) return;
    setGeneratedRamRevising(true);
    try {
      const res = await generatedRamsApi.revise(editingGeneratedRamId, { comments });
      setEditingGeneratedRamData(res.data);
    } catch (err) {
      console.error('Error revising generated RAM:', err);
      setError('Failed to revise generated RAMS');
    } finally {
      setGeneratedRamRevising(false);
    }
  }, [editingGeneratedRamId]);

  const handleSaveGeneratedRamPdf = useCallback(async (generatedRamId, fileName) => {
    setGeneratedRamPdfSaving(prev => ({ ...prev, [generatedRamId]: true }));
    try {
      const res = await generatedRamsApi.savePdf(generatedRamId, { file_name: fileName || '' });
      if (res?.data) {
        setRams(prev => [res.data, ...prev]);
      }
      setError(null);
    } catch (err) {
      console.error('Error saving generated RAM as PDF:', err);
      setError(err?.response?.data?.error || 'Failed to save generated RAMS to PDF');
    } finally {
      setGeneratedRamPdfSaving(prev => {
        const next = { ...prev };
        delete next[generatedRamId];
        return next;
      });
    }
  }, []);

  const handleEditHospitalChange = useCallback((key, value) => {
    setEditingHospitalFields(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setUploadFiles([]);
    setUploadInProgress(false); // Reset the flag when dialog closes
    setUploadProgress({}); // Clear progress tracking
  };

  const handleFileDrop = (droppedFiles) => {
    const { files: acceptedFiles, warning } = limitFilesForUpload(droppedFiles || []);
    if (warning) {
      alert(warning);
      return;
    }

    const validFiles = acceptedFiles.filter(file => file.type === 'application/pdf');
    const invalidFiles = acceptedFiles.filter(file => file.type !== 'application/pdf');
    
    if (invalidFiles.length > 0) {
      setError(`${invalidFiles.length} file(s) were rejected. Only PDF files are allowed.`);
    }

    if (validFiles.length > 0) {
      // Rename files with UUID to avoid conflicts
      const renamedFiles = validFiles.map(file => renameFile(file));
      setUploadFiles(prevFiles => [...prevFiles, ...renamedFiles]);
      setError(null); // Clear any previous errors
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setUploadFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
    // Clear progress for the removed file
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[indexToRemove];
      return newProgress;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  const handleUploadRAM = async () => {
    if (!uploadFiles.length || uploading || uploadInProgress) {
      console.log('Upload blocked:', { uploadFiles: uploadFiles.length, uploading, uploadInProgress });
      return; // Prevent multiple uploads
    }

    console.log('Starting RAMS upload for', uploadFiles.length, 'files...');
    
    try {
      setUploading(true);
      setUploadInProgress(true);
      setError(null);
      
      const uploadResults = [];
      const initialProgress = {};
      
      // Initialize progress tracking for all files
      uploadFiles.forEach((file, index) => {
        initialProgress[index] = { status: 'pending', fileName: file.name };
      });
      setUploadProgress(initialProgress);
      
      // Upload files sequentially
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        console.log(`Uploading file ${i + 1}/${uploadFiles.length}: ${file.name}`);
        
        // Update progress to uploading
        setUploadProgress(prev => ({
          ...prev,
          [i]: { ...prev[i], status: 'uploading' }
        }));
        
        try {
          const formData = new FormData();
          formData.append('file', file);

          // Use the filename as the name, removing the .pdf extension
          const ramName = file.name.replace(/\.pdf$/i, '');
          formData.append('name', ramName);
          formData.append('description', `RAMS document: ${ramName}`);

          console.log(`Calling ramsApi.upload for ${file.name}...`);
          const result = await ramsApi.upload(projectId, formData);
          
          uploadResults.push({ file: file.name, success: true, data: result });
          
          // Update progress to completed
          setUploadProgress(prev => ({
            ...prev,
            [i]: { ...prev[i], status: 'completed' }
          }));
          
          console.log(`Upload completed successfully for ${file.name}`);
          
        } catch (err) {
          console.error(`Error uploading ${file.name}:`, err);
          
          // Update progress to failed
          setUploadProgress(prev => ({
            ...prev,
            [i]: { ...prev[i], status: 'failed', error: err.response?.data?.error || 'Upload failed' }
          }));
          
          uploadResults.push({ file: file.name, success: false, error: err.response?.data?.error || 'Upload failed' });
        }
      }
      
      // Check results
      const successfulUploads = uploadResults.filter(result => result.success);
      const failedUploads = uploadResults.filter(result => !result.success);
      
      if (successfulUploads.length > 0) {
        console.log(`Successfully uploaded ${successfulUploads.length} file(s)`);
        
        if (failedUploads.length > 0) {
          setError(`${successfulUploads.length} file(s) uploaded successfully, but ${failedUploads.length} failed. Check the file list for details.`);
        } else {
          setError(null);
        }
        
        setUploadDialogOpen(false);
        setUploadFiles([]);
        setUploadProgress({});
        await fetchRAMData();
        
      } else {
        setError('All uploads failed. Please check the file list for error details.');
      }
      
    } catch (err) {
      console.error('Error in upload process:', err);
      setError('Failed to upload RAMS documents');
    } finally {
      setUploading(false);
      setUploadInProgress(false);
      console.log('Upload process finished');
    }
  };

  const handleSignatureDialogOpen = (ram) => {
    console.log('handleSignatureDialogOpen called with:', ram);
    console.log('Setting selectedRam to:', ram);
    console.log('Opening signature dialog...');
    console.log('Current signatureDialogOpen state:', signatureDialogOpen);
    console.log('Current selectedRam state:', selectedRam);
    
    // Clear any existing errors
    setError(null);
    setSignatureError(null);
    
    setSelectedRam(ram);
    setSignatureDialogOpen(true);
    setSigningFor('self');
    setSelectedWorker('');
    
    // Resize signature canvas after dialog opens
    setTimeout(() => {
      if (signatureRef.current) {
        signatureRef.current.getCanvas().width = signatureRef.current.getCanvas().offsetWidth;
        signatureRef.current.getCanvas().height = 200;
        signatureRef.current.clear();
      }
    }, 100);
    
    // Log after setting state
    console.log('Signature dialog state should now be:', {
      selectedRam: ram,
      signatureDialogOpen: true,
      signingFor: 'self'
    });
  };

  const handleSubmitSignature = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setSignatureError('Please provide a signature');
      return;
    }

    let workerId, workerName;
    
    if (signingFor === 'self') {
      workerId = currentUser.worker_id;
      workerName = currentUser.name;
    } else {
      // For signing for someone else, we need a name
      if (!selectedWorker || selectedWorker.trim() === '') {
        setSignatureError('Please enter the name of the person you\'re signing for');
        return;
      }
      workerName = selectedWorker.trim();
      // We'll use a placeholder worker_id for non-workers, backend should handle this
      workerId = null;
    }

    try {
      setSigning(true);
      setSignatureError(null); // Clear any previous errors
      
      // Get signature as data URL
      const signatureDataURL = signatureRef.current.toDataURL();
      
      const signatureData = {
        worker_id: workerId,
        worker_name: workerName, // Include the name for custom entries
        signature_data: signatureDataURL,
        signed_by_worker_id: currentUser.worker_id
      };
      
      await ramsApi.submitSignature(selectedRam.ram_id, signatureData);
      
      handleSignatureDialogClose();
      await fetchRAMData();
      
      // Notify parent component that signatures have been updated
      if (onSignatureUpdate) {
        onSignatureUpdate();
      }
      
    } catch (err) {
      console.error('Error submitting signature:', err);
      setSignatureError('Failed to submit signature');
    } finally {
      setSigning(false);
    }
  };

  const handlePreviewRAM = async (ram) => {
    try {
      console.log('Getting preview URL for RAM:', ram.ram_id);
      console.log('RAM object:', ram);
      
      const response = await ramsApi.getDownloadUrl(ram.ram_id);
      console.log('Full response object:', response);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      console.log('Response data type:', typeof response.data);
      console.log('Response data:', response.data);
      
      // Try multiple ways to access the URL
      let previewUrl = null;
      if (response.data) {
        if (typeof response.data === 'string') {
          // Response data might be a string URL directly
          previewUrl = response.data;
          console.log('URL found as string:', previewUrl);
        } else if (response.data.url) {
          // Standard object with url property
          previewUrl = response.data.url;
          console.log('URL found in data.url:', previewUrl);
        } else if (response.data.download_url) {
          // Alternative property name
          previewUrl = response.data.download_url;
          console.log('URL found in data.download_url:', previewUrl);
        }
      }
      
      console.log('Final preview URL:', previewUrl);
      
      if (previewUrl && previewUrl.trim() !== '') {
        setPreviewUrl(previewUrl);
        setPreviewDialogOpen(true);
        console.log('Preview URL set successfully:', previewUrl);
      } else {
        console.error('No valid URL found in response');
        console.error('Available properties in response.data:', Object.keys(response.data || {}));
        setError('Failed to get preview URL - no URL returned');
      }
    } catch (err) {
      console.error('Error getting RAM preview:', err);
      console.error('Error response:', err.response);
      if (err.response && err.response.data) {
        setError(`Failed to load RAMS preview: ${err.response.data.error || err.response.data.message || 'Unknown error'}`);
      } else {
        setError('Failed to load RAMS preview');
      }
    }
  };

  const handleDownloadRAM = trackDownload(async (ram) => {
    try {
      // Use download-with-signatures endpoint which appends a signatures page on-the-fly
      const response = await ramsApi.downloadWithSignatures(ram.ram_id);
      if (response.data instanceof Blob) {
        // Check if the blob is actually JSON (no signatures case)
        if (response.data.type === 'application/json') {
          try {
            const text = await response.data.text();
            const json = JSON.parse(text);
            if (json.url) {
              window.open(json.url, '_blank');
              return;
            }
          } catch (_) { /* not JSON, treat as PDF */ }
        }
        // Direct file download (signatures were appended)
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = ram.file_name || 'rams.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error downloading RAM:', err);
      // Fall back to regular download URL
      try {
        const response = await ramsApi.getDownloadUrl(ram.ram_id);
        window.open(response.data.url, '_blank');
      } catch (fallbackErr) {
        setError('Failed to download RAMS');
      }
    }
  });

  const handleViewSignature = (signature) => {
    console.log('Viewing signature:', signature);
    console.log('Signature data type:', typeof signature.signature_data);
    console.log('Signature data length:', signature.signature_data?.length);
    console.log('Signature data starts with:', signature.signature_data?.substring(0, 30));
    setPreviewSignature(signature);
    setSignaturePreviewOpen(true);
  };

  const handleDeleteDialogOpen = (ram) => {
    setRamToDelete(ram);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setRamToDelete(null);
  };

  const handleDeleteRAM = async () => {
    if (!ramToDelete || deleting) return;

    try {
      setDeleting(true);
      
      await ramsApi.delete(ramToDelete.ram_id);
      
      handleDeleteDialogClose();
      await fetchRAMData();
      
    } catch (err) {
      console.error('Error deleting RAM:', err);
      setError('Failed to delete RAMS document');
    } finally {
      setDeleting(false);
    }
  };

  // Reusable Signature Dialog Component - defined after all handlers
  const SignatureDialog = useMemo(() => (
    <Dialog 
      open={signatureDialogOpen}
      onClose={handleSignatureDialogClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>Sign RAMS Document</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Debug info */}
          {process.env.NODE_ENV === 'development' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="caption">
                Debug: Blocking UI - RAMs={rams.length}, UserSignatures={
                  signatures.filter(sig => sig.worker_id === currentUser?.worker_id).length
                }, AllSigned={
                  rams.length > 0 && signatures.filter(sig => sig.worker_id === currentUser?.worker_id).length === rams.length
                }
              </Typography>
            </Alert>
          )}
          
          {/* Signature-specific error display */}
          {signatureError && (
            <Alert severity="error" onClose={() => setSignatureError(null)} sx={{ mb: 2 }}>
              {signatureError}
            </Alert>
          )}
          
          {/* Signing options */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Signing for</InputLabel>
            <Select
              value={signingFor}
              onChange={handleSigningForChange}
              label="Signing for"
            >
              <MenuItem value="self">Myself</MenuItem>
              <MenuItem value="other">Someone else</MenuItem>
            </Select>
          </FormControl>

          {signingFor === 'other' && (
            <TextField
              fullWidth
              label="Full Name"
              value={selectedWorker}
              onChange={handleWorkerNameChange}
              placeholder="Enter the name of the person you're signing for"
              sx={{ mb: 3 }}
              autoComplete="off"
              spellCheck={false}
            />
          )}

          {/* Signature canvas */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Signature
            </Typography>
            <Box
              sx={{
                border: '2px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                overflow: 'hidden',
                mb: 2,
                position: 'relative',
                width: '100%',
                height: { xs: '150px', sm: '180px', md: '200px' }
              }}
            >
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                  style: { 
                    width: '100%', 
                    height: '100%',
                    touchAction: 'none',
                    display: 'block',
                    border: 'none',
                    outline: 'none'
                  }
                }}
                backgroundColor="rgb(255, 255, 255)"
                penColor="black"
                dotSize={isMobile ? 2 : 1.5}
                velocityFilterWeight={0.7}
                minWidth={isMobile ? 1.2 : 0.8}
                maxWidth={isMobile ? 3.5 : 2.5}
              />
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearSignature}
              startIcon={<ClearIcon />}
            >
              Clear Signature
            </Button>
          </Paper>

          <Alert severity="info">
            By signing this document, you acknowledge that you have read and understood 
            the Risk Assessment Method Statement and agree to follow all safety procedures outlined.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={handleSignatureDialogClose} fullWidth={isMobile}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmitSignature}
          variant="contained"
          disabled={signing}
          startIcon={signing ? <CircularProgress size={20} /> : <CheckIcon />}
          fullWidth={isMobile}
        >
          {signing ? 'Submitting...' : 'Submit Signature'}
        </Button>
      </DialogActions>
    </Dialog>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [signatureDialogOpen, selectedRam, signatureError, signingFor, selectedWorker, signing, isMobile, handleSignatureDialogClose, handleSigningForChange, handleWorkerNameChange, handleClearSignature]);

  // Reusable Preview Dialog Component
  const PreviewDialog = useMemo(() => (
    <Dialog 
      open={previewDialogOpen} 
      onClose={() => setPreviewDialogOpen(false)}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        RAMS Document Preview
        <IconButton
          onClick={() => setPreviewDialogOpen(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <ClearIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {previewUrl ? (
          <Box>
            <iframe
              src={`${previewUrl}#toolbar=1`}
              style={{
                width: '100%',
                height: isMobile ? 'calc(100vh - 180px)' : '65vh',
                border: 'none'
              }}
              title="RAMS Document Preview"
              onLoad={() => console.log('Iframe loaded successfully')}
              onError={(e) => {
                console.error('Iframe error:', e);
                setError('Preview failed. Try opening in a new tab.');
              }}
            />
            <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Having trouble viewing? 
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => window.open(previewUrl, '_blank')}
                startIcon={<FileDownloadIcon />}
              >
                Open in New Tab
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading preview...
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  ), [previewDialogOpen, previewUrl, isMobile]);

  // Signature Preview Dialog Component
  const SignaturePreviewDialog = useMemo(() => (
    <Dialog 
      open={signaturePreviewOpen} 
      onClose={() => setSignaturePreviewOpen(false)}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        Signature Preview
        <IconButton
          onClick={() => setSignaturePreviewOpen(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <ClearIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {previewSignature ? (
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {previewSignature.signer_name || previewSignature.worker_name || 'Unknown Signer'}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Signed on {format(new Date(previewSignature.signed_date), 'PPpp')}
            </Typography>
            
            {(() => {
              const signedByWorker = workers.find(w => w.worker_id === previewSignature.signed_by_worker_id);
              const signedByName = signedByWorker ? signedByWorker.name : 'Unknown';
              const signerName = previewSignature.signer_name || previewSignature.worker_name || 'Unknown';
              
              return previewSignature.signed_by_worker_id !== previewSignature.worker_id && signedByName !== signerName && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Signature collected by: {signedByName}
                </Typography>
              );
            })()}
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={(theme) => ({ 
              border: '2px solid',
              borderColor: theme.palette.divider,
              borderRadius: 1,
              p: 2,
              bgcolor: 'background.paper',
              maxWidth: 400,
              mx: 'auto'
            })}>
              {previewSignature.signature_data ? (
                <img 
                  src={
                    previewSignature.signature_data.startsWith('data:') 
                      ? previewSignature.signature_data 
                      : `data:image/png;base64,${previewSignature.signature_data}`
                  }
                  alt="Signature"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    margin: '0 auto',
                    backgroundColor: 'white'
                  }}
                  onError={(e) => {
                    console.error('Error loading signature image:', e);
                    console.log('Signature data:', previewSignature.signature_data?.substring(0, 100) + '...');
                  }}
                  onLoad={() => {
                    console.log('Signature image loaded successfully');
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Signature data not available
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading signature...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSignaturePreviewOpen(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  ), [signaturePreviewOpen, previewSignature, workers, isMobile]);

  // Delete Confirmation Dialog Component
  const DeleteConfirmationDialog = useMemo(() => (
    <Dialog 
      open={deleteDialogOpen} 
      onClose={handleDeleteDialogClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon sx={{ color: 'error.main' }} />
          Delete RAMS Document
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            This action cannot be undone!
          </Typography>
          <Typography variant="body2">
            Deleting this RAMS document will permanently remove it and all associated signatures. 
            This may affect project compliance and worker access.
          </Typography>
        </Alert>
        
        {ramToDelete && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete the following RAMS document?
            </Typography>
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  {ramToDelete.file_name}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Uploaded on {format(new Date(ramToDelete.uploaded_date), 'PPP')}
              </Typography>
              
              {(() => {
                const ramSignatures = signatures.filter(sig => sig.ram_id === ramToDelete.ram_id);
                return ramSignatures.length > 0 && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                    ⚠ This document has {ramSignatures.length} signature{ramSignatures.length !== 1 ? 's' : ''} that will also be deleted
                  </Typography>
                );
              })()}
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button onClick={handleDeleteDialogClose} fullWidth={isMobile}>
          Cancel
        </Button>
        <Button 
          onClick={handleDeleteRAM}
          variant="contained"
          color="error"
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          fullWidth={isMobile}
        >
          {deleting ? 'Deleting...' : 'Delete RAMS'}
        </Button>
      </DialogActions>
    </Dialog>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [deleteDialogOpen, ramToDelete, signatures, deleting, isMobile, handleDeleteDialogClose]);

  // Show blocking message if RAMS signature is required
  const ramSignatureRequired = isRAMSignatureRequiredMemo;
  
  if (ramSignatureRequired) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
        <Card sx={{ 
          maxWidth: { xs: '100%', sm: 600 }, 
          mx: 'auto', 
          border: '2px solid', 
          borderColor: 'warning.main' 
        }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              mb: 3,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 }
            }}>
              <WarningIcon sx={{ 
                fontSize: { xs: 40, sm: 48 }, 
                color: 'warning.main', 
                mr: { xs: 0, sm: 2 }
              }} />
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                color="warning.main" 
                fontWeight="bold"
                textAlign="center"
              >
                Action Required
              </Typography>
            </Box>
            
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
              RAMS Signatures Required ({rams.filter(ram => {
                const userSignatures = signatures.filter(sig => 
                  sig.worker_id === currentUser.worker_id && sig.ram_id === ram.ram_id
                );
                return userSignatures.length > 0;
              }).length} of {rams.length} completed)
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: 3,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                lineHeight: { xs: 1.4, sm: 1.5 }
              }}
            >
              You must review and sign ALL Risk Assessment and Method Statement (RAMS) documents 
              before accessing other project information.
            </Typography>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2">Loading RAMS documents...</Typography>
              </Box>
            ) : rams.length === 0 ? (
              <Alert severity="info">
                No RAMS documents have been uploaded yet. Please contact project administration.
              </Alert>
            ) : (
              <Box>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Please review all RAMS documents below and sign each one:
                </Typography>
                
                {/* Show all RAMS documents */}
                <Stack spacing={2}>
                  {rams.map((ram, index) => {
                    // Check if current user has signed this RAM
                    const userSignatures = signatures.filter(sig => 
                      sig.worker_id === currentUser.worker_id && sig.ram_id === ram.ram_id
                    );
                    const isSigned = userSignatures.length > 0;
                    
                    return (
                      <Paper 
                        key={ram.ram_id} 
                        sx={{ 
                          p: 3, 
                          border: '1px solid', 
                          borderColor: isSigned ? 'success.main' : 'grey.300',
                          bgcolor: isSigned ? 'success.50' : 'background.paper'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <DescriptionIcon sx={{ mr: 1, color: isSigned ? 'success.main' : 'primary.main' }} />
                          <Typography variant="h6" fontWeight="bold">
                            {ram.file_name}
                          </Typography>
                          {isSigned && (
                            <Chip 
                              label="Signed" 
                              color="success" 
                              size="small" 
                              sx={{ ml: 2 }}
                              icon={<CheckIcon />}
                            />
                          )}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Uploaded on {format(new Date(ram.uploaded_date), 'PPP')}
                        </Typography>
                        
                        {/* Preview and Download buttons */}
                        <Box sx={{ 
                          mt: 2, 
                          display: 'flex', 
                          gap: { xs: 2, sm: 1 }, 
                          flexDirection: { xs: 'column', sm: 'row' },
                          justifyContent: 'center',
                          mb: 2
                        }}>
                          <Button
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handlePreviewRAM(ram)}
                            fullWidth={isMobile}
                            sx={{ py: { xs: 1.5, sm: 1 } }}
                          >
                            Preview RAMS
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<FileDownloadIcon />}
                            onClick={() => handleDownloadRAM(ram)}
                            fullWidth={isMobile}
                            sx={{ py: { xs: 1.5, sm: 1 } }}
                          >
                            Download RAMS
                          </Button>
                        </Box>
                        
                        {/* Sign button - keep available so users can add signatures for someone else */}
                        {canSignRAM && (
                          <Box sx={{ textAlign: 'center' }}>
                            <Button
                              variant="contained"
                              size="large"
                              onClick={() => handleSignatureDialogOpen(ram)}
                              startIcon={<AssignmentIcon />}
                              sx={{ 
                                minWidth: { xs: '100%', sm: 200 },
                                fontSize: { xs: '1rem', sm: '0.875rem' },
                                py: { xs: 1.5, sm: 1 }
                              }}
                            >
                              Sign This RAMS
                            </Button>
                          </Box>
                        )}
                        
                        {/* Show signature info if signed */}
                        {isSigned && userSignatures.length > 0 && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Signed on {format(new Date(userSignatures[0].signed_date), 'PPP')}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
                
                {/* Progress indicator */}
                {(() => {
                  const userSignatures = signatures.filter(sig => sig.worker_id === currentUser.worker_id);
                  const signedRamIds = userSignatures.map(sig => sig.ram_id);
                  const signedCount = rams.filter(ram => signedRamIds.includes(ram.ram_id)).length;
                  
                  return (
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Progress: {signedCount} of {rams.length} RAMS documents signed
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={(signedCount / rams.length) * 100} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  );
                })()}
              </Box>
            )}
          </CardContent>
        </Card>
        
        {/* Error display in blocking UI */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
            {error}
          </Alert>
        )}
        
        {/* Reusable dialogs - available in blocking UI */}
        {SignatureDialog}
        {PreviewDialog}
        {SignaturePreviewDialog}
        {DeleteConfirmationDialog}
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Debug Info - Remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption">
            Debug: Role={currentUser?.role}, Status={projectStatus}, CanSign={canSignRAM.toString()}, 
            RAMs={rams.length}, Signatures={signatures.length}, RequireSignature={isRAMSignatureRequired().toString()},
            UserSignatures={signatures.filter(sig => sig.worker_id === currentUser?.worker_id).length}
          </Typography>
        </Alert>
      )}
      
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2
      }}>
        <Typography variant="h5" fontWeight="bold">
          
        </Typography>
        
        {canUploadRAM && (
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={handleUploadDialogOpen}
            sx={{ minWidth: { xs: '100%', md: 'auto' } }}
          >
            Upload RAMS Documents
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* AI RAMS Generator (Office only) */}
      <RAMSGeneratorSection
        projectId={projectId}
        currentUser={currentUser}
        onGenerated={handleGeneratedRamsRefresh}
      />

      {/* Generated RAMS List (Office only) */}
      {currentUser?.role === 'Office' && generatedRams.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            AI Generated RAMS ({generatedRams.length})
          </Typography>
          <Stack spacing={2}>
            {generatedRams.map((gram) => (
              <Card key={gram.generated_ram_id} variant="outlined">
                <CardContent sx={{ pb: editingGeneratedRamId === gram.generated_ram_id ? 2 : '16px !important' }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => handleEditGeneratedRam(gram)}
                  >
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Generated RAMS
                        {gram.contracts_manager_name ? ` — CM: ${gram.contracts_manager_name}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created {format(new Date(gram.created_date), 'PPP')}
                        {gram.last_modified_date && gram.last_modified_date !== gram.created_date
                          ? ` · Modified ${format(new Date(gram.last_modified_date), 'PPP')}`
                          : ''}
                        {' · '}
                        {(gram.risk_assessment || []).length} hazard{(gram.risk_assessment || []).length !== 1 ? 's' : ''}
                        {' · '}
                        {(gram.method_statements || []).length} method statement{(gram.method_statements || []).length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={generatedRamPdfSaving[gram.generated_ram_id] ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
                        disabled={!!generatedRamPdfSaving[gram.generated_ram_id]}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveGeneratedRamPdf(gram.generated_ram_id);
                        }}
                      >
                        {generatedRamPdfSaving[gram.generated_ram_id] ? 'Saving...' : 'Save to PDF'}
                      </Button>
                      <Chip
                        label={editingGeneratedRamId === gram.generated_ram_id ? 'Collapse' : 'Edit'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>

                  <Collapse in={editingGeneratedRamId === gram.generated_ram_id} timeout="auto">
                    {editingGeneratedRamData && editingGeneratedRamId === gram.generated_ram_id && (
                      <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <GeneratedRAMSEditor
                          data={editingGeneratedRamData}
                          officeWorkers={allOfficeWorkers}
                          siteWorkers={allSiteWorkers}
                          contractsManagerId={editingContractsManagerId}
                          supervisorId={editingSupervisorId}
                          responsiblePeopleIds={editingResponsiblePeopleIds}
                          hospitalFields={editingHospitalFields}
                          onDataChange={setEditingGeneratedRamData}
                          onHospitalChange={handleEditHospitalChange}
                          onContractsManagerChange={setEditingContractsManagerId}
                          onSupervisorChange={setEditingSupervisorId}
                          onResponsiblePeopleChange={setEditingResponsiblePeopleIds}
                          onSave={handleSaveGeneratedRam}
                          onSavePdf={(fileName) => handleSaveGeneratedRamPdf(gram.generated_ram_id, fileName)}
                          onDelete={() => handleDeleteGeneratedRam(gram.generated_ram_id)}
                          onRevise={handleReviseGeneratedRam}
                          saving={generatedRamSaving}
                          savingPdf={!!generatedRamPdfSaving[gram.generated_ram_id]}
                          revising={generatedRamRevising}
                          generatedRamId={gram.generated_ram_id}
                          defaultPdfName={`RAMS - ${projectId}`}
                        />
                      </Box>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      )}

      {/* RAMS Documents */}
      {rams.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No RAMS documents uploaded
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canUploadRAM 
              ? 'Upload RAMS documents to get started'
              : 'RAMS documents will appear here once uploaded by project administrators'
            }
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {rams.map((ram) => (
            <Card key={ram.ram_id} sx={{ overflow: 'visible' }}>
              <CardContent>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6" fontWeight="bold">
                        {ram.file_name}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Uploaded on {format(new Date(ram.uploaded_date), 'PPP')}
                    </Typography>
                    
                    <Box sx={{ 
                      mt: 2, 
                      display: 'flex', 
                      gap: { xs: 1, sm: 1 }, 
                      flexDirection: { xs: 'column', sm: 'row' },
                      flexWrap: 'wrap' 
                    }}>
                      <Button
                        size={isMobile ? "medium" : "small"}
                        startIcon={<VisibilityIcon />}
                        onClick={() => handlePreviewRAM(ram)}
                        fullWidth={isMobile}
                        sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
                      >
                        Preview
                      </Button>
                      <Button
                        size={isMobile ? "medium" : "small"}
                        startIcon={<FileDownloadIcon />}
                        onClick={() => handleDownloadRAM(ram)}
                        fullWidth={isMobile}
                        sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
                      >
                        Download
                      </Button>
                      {canSignRAM && (
                        <Button
                          size={isMobile ? "medium" : "small"}
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => handleSignatureDialogOpen(ram)}
                          fullWidth={isMobile}
                          sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
                        >
                          Sign RAMS
                        </Button>
                      )}
                      {canUploadRAM && (
                        <Button
                          size={isMobile ? "medium" : "small"}
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteDialogOpen(ram)}
                          fullWidth={isMobile}
                          sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* RAMS Signatures */}
                {(() => {
                  const ramSignatures = signatures.filter(sig => sig.ram_id === ram.ram_id);
                  return ramSignatures.length > 0 && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="h6" gutterBottom>
                        Signatures ({ramSignatures.length})
                      </Typography>
                      
                      <List dense>
                        {ramSignatures.map((signature) => {
                          const signedByWorker = workers.find(w => w.worker_id === signature.signed_by_worker_id);
                          const signerName = signature.signer_name || signature.worker_name || 'Unknown';
                          const signedByName = signedByWorker ? signedByWorker.name : 'Unknown';
                          const signedDate = new Date(signature.signed_date);
                          
                          return (
                            <ListItem 
                              key={signature.signature_id} 
                              sx={{ 
                                px: 0,
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                gap: { xs: 1, sm: 0 }
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                width: { xs: '100%', sm: 'auto' },
                                flex: { xs: 'none', sm: 1 }
                              }}>
                                <ListItemIcon sx={{ minWidth: { xs: 35, sm: 56 } }}>
                                  <CheckIcon sx={{ color: 'success.main' }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={signerName}
                                  secondary={
                                    `Signed on ${format(signedDate, isMobile ? 'MMM dd, yyyy' : 'PPp')}${
                                      signature.signed_by_worker_id !== signature.worker_id && signedByName !== signerName 
                                        ? ` by ${signedByName}` 
                                        : ''
                                    }`
                                  }
                                  secondaryTypographyProps={{
                                    sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                                  }}
                                />
                              </Box>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                                width: { xs: '100%', sm: 'auto' },
                                pl: { xs: 5, sm: 0 }
                              }}>
                                <Chip
                                  label="Signed"
                                  color="success"
                                  size="small"
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleViewSignature(signature)}
                                  sx={{ minWidth: 'auto', px: { xs: 2, sm: 1 } }}
                                >
                                  View
                                </Button>
                              </Box>
                            </ListItem>
                          );
                        })}
                      </List>
                      
                      {/* Show pending signatures for reference */}
                      {(() => {
                        const signedWorkerIds = ramSignatures.map(sig => sig.worker_id).filter(Boolean);
                        const pendingWorkers = workers.filter(worker => !signedWorkerIds.includes(worker.worker_id));
                        
                        return pendingWorkers.length > 0 && canSignRAM && (
                          <>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                              Pending Signatures
                            </Typography>
                            <List dense>
                              {pendingWorkers.map((worker) => (
                                <ListItem 
                                  key={worker.worker_id} 
                                  sx={{ 
                                    px: 0,
                                    flexDirection: { xs: 'row', sm: 'row' },
                                    alignItems: 'center'
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: { xs: 35, sm: 56 } }}>
                                    <ClearIcon sx={{ color: 'error.main' }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={worker.name}
                                    secondary="Signature pending"
                                    secondaryTypographyProps={{
                                      sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                                    }}
                                  />
                                  <Chip
                                    label="Pending"
                                    color="error"
                                    size="small"
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        );
                      })()}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Upload RAMS Documents</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box
              {...getRootProps()}
              sx={(theme) => ({
                border: '2px dashed',
                borderColor: isDragActive ? theme.palette.primary.main : theme.palette.divider,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: isDragActive ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                cursor: 'pointer',
                mb: 2,
              })}
            >
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop the PDF files here' : 'Drag and drop PDF files here'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or click to select files (multiple files allowed)
              </Typography>
            </Box>
            
            {/* Selected Files List */}
            {uploadFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Selected Files ({uploadFiles.length})
                </Typography>
                <Stack spacing={1}>
                  {uploadFiles.map((file, index) => {
                    const progress = uploadProgress[index];
                    return (
                      <Paper 
                        key={index} 
                        sx={(theme) => ({ 
                          p: 2, 
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: progress?.status === 'failed' ? theme.palette.error.main : theme.palette.divider
                        })}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                            <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" noWrap fontWeight="medium">
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Status indicator */}
                            {progress?.status === 'uploading' && (
                              <CircularProgress size={20} />
                            )}
                            {progress?.status === 'completed' && (
                              <CheckIcon sx={{ color: 'success.main' }} />
                            )}
                            {progress?.status === 'failed' && (
                              <WarningIcon sx={{ color: 'error.main' }} />
                            )}
                            
                            {/* Remove button - only show if not uploading */}
                            {!uploading && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(index)}
                                sx={{ color: 'error.main' }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                        
                        {/* Error message for failed uploads */}
                        {progress?.status === 'failed' && progress.error && (
                          <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                            {progress.error}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleUploadDialogClose} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button 
            onClick={handleUploadRAM}
            variant="contained"
            disabled={!uploadFiles.length || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
            fullWidth={isMobile}
          >
            {uploading 
              ? `Uploading ${uploadFiles.length} file${uploadFiles.length !== 1 ? 's' : ''}...` 
              : `Upload ${uploadFiles.length || ''} RAMS Document${uploadFiles.length !== 1 ? 's' : ''}`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reusable dialogs */}
      {SignatureDialog}
      {PreviewDialog}
      {SignaturePreviewDialog}
      {DeleteConfirmationDialog}
    </Box>
  );
};

export default ProjectRAMTab;
